"use client";

import {
  listAdminCoursesApiV1AdminCoursesGet,
  getAdminCourseApiV1AdminCoursesCourseIdGet,
  listAdminOrganizationsApiV1AdminOrganizationsGet,
} from "@/src/api/sdk.gen";
import { apiFetch } from "@/src/lib/api-fetch";
import { formatApiErrorMessage } from "@/src/lib/error-utils";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link } from "@/src/lib/next-compat";
import {  useNavigate  } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  ChevronRight,
  FileText,
  Globe,
  GraduationCap,
  Loader2,
  Lock,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
} from "lucide-react";
import { SortableList } from "./Sortable";
import { GenerateFromDocModal } from "./GenerateFromDocModal";

type CourseSummary = { id: number; title: string; isPublic?: boolean; orgNames?: string[] };
type Org = { id: number; name: string };
type Level = { id: number; name: string; level_number: number };
type LessonNode = {
  id: number;
  title: string;
  lesson_type: string | null;
  generated_lessons: { id: number; structured_content: unknown } | null;
};
type ModuleNode = {
  id: number;
  title: string;
  levels: { id: number } | null;
  lessons: LessonNode[];
};
type CourseDetail = { id: number; title: string; levels: Level[]; modules: ModuleNode[] };
type Kind = "levels" | "modules" | "lessons";

const fieldClass =
  "rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100";
const addBtnClass =
  "inline-flex items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-50";

export default function CourseBuilder({ initialCourseId = null }: { initialCourseId?: number | null }) {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<CourseSummary[]>([]);
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(initialCourseId);
  const [detail, setDetail] = useState<CourseDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showNewCourse, setShowNewCourse] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  // new-course form
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState<"public" | "restricted">("public");
  const [selectedOrgIds, setSelectedOrgIds] = useState<number[]>([]);
  const [creating, setCreating] = useState(false);

  const loadCourses = useCallback(async () => {
    const { data, error } = await listAdminCoursesApiV1AdminCoursesGet();
    if (!error && data) setCourses(data as unknown as CourseSummary[]);
  }, []);

  const loadDetail = useCallback(async (id: number) => {
    setLoadingDetail(true);
    try {
      const { data, error } = await getAdminCourseApiV1AdminCoursesCourseIdGet({
        path: { course_id: id },
      });
      if (!error && data) setDetail(data as unknown as CourseDetail);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    loadCourses();
    listAdminOrganizationsApiV1AdminOrganizationsGet().then(({ data, error }) => {
      if (!error && data) setOrgs(data as unknown as Org[]);
    });
  }, [loadCourses]);

  useEffect(() => {
    if (selectedCourseId != null) loadDetail(selectedCourseId);
    else setDetail(null);
  }, [selectedCourseId, loadDetail]);

  // Keep the selected course in the URL so returning from a lesson restores it.
  const selectCourse = (id: number | null) => {
    setSelectedCourseId(id);
    setError(null);
    navigate(id != null ? `/admin/courses/builder?course=${id}` : "/admin/courses/builder");
  };

  const openLesson = useCallback(
    (lessonId: number) => {
      const suffix = selectedCourseId != null ? `?course=${selectedCourseId}` : "";
      navigate(`/admin/courses/builder/lesson/${lessonId}${suffix}`);
    },
    [, selectedCourseId],
  );

  const reloadDetail = useCallback(() => {
    if (selectedCourseId != null) loadDetail(selectedCourseId);
  }, [selectedCourseId, loadDetail]);

  const toggleExpand = (id: number) =>
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const post = async (url: string, body: unknown) => {
    setError(null);
    const res = await apiFetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = formatApiErrorMessage(data, `Request failed (${res.status})`);
      setError(msg);
      throw new Error(msg);
    }
    return data;
  };

  const createCourse = async () => {
    if (!title.trim()) {
      setError("Course title is required.");
      return;
    }
    const isPublic = visibility === "public";
    if (!isPublic && selectedOrgIds.length === 0) {
      setError("Please select at least one organization for a restricted course.");
      return;
    }
    setCreating(true);
    try {
      const created = await post("admin/interactive/courses", {
        title: title.trim(),
        description: description.trim() || undefined,
        is_public: isPublic,
        organization_ids: !isPublic ? selectedOrgIds : [],
      });
      setTitle("");
      setDescription("");
      setVisibility("public");
      setSelectedOrgIds([]);
      setShowNewCourse(false);
      await loadCourses();
      selectCourse(created.id);
    } catch {
      /* error surfaced */
    } finally {
      setCreating(false);
    }
  };

  const addLevel = (name: string) =>
    post("admin/interactive/levels", { course_id: selectedCourseId, name }).then(reloadDetail);
  const addModule = (levelId: number, moduleTitle: string) =>
    post("admin/interactive/modules", {
      course_id: selectedCourseId,
      level_id: levelId,
      title: moduleTitle,
    }).then((created) => {
      reloadDetail();
      if (created?.id) setExpanded((s) => new Set(s).add(created.id));
    });
  const addLesson = (moduleId: number, lessonTitle: string) =>
    // Just add it to the list — don't navigate into the builder; the user opens
    // a lesson by clicking it.
    post("admin/interactive/lessons", { module_id: moduleId, title: lessonTitle }).then(
      reloadDetail,
    );

  const mutate = async (url: string, method: "PATCH" | "DELETE", body?: unknown) => {
    setError(null);
    const res = await apiFetch(url, {
      method,
      ...(body ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) } : {}),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setError(formatApiErrorMessage(data, `Request failed (${res.status})`));
      return;
    }
    reloadDetail();
  };

  const renameItem = (kind: Kind, id: number, field: "name" | "title", current: string) => {
    const v = window.prompt("Rename:", current);
    if (v != null && v.trim() && v.trim() !== current) {
      mutate(`/api/admin/interactive/${kind}/${id}`, "PATCH", { [field]: v.trim() });
    }
  };
  const removeItem = (kind: Kind, id: number, label: string) => {
    if (!window.confirm(`Delete this ${label}? Everything inside it is deleted too.`)) return;
    mutate(`/api/admin/interactive/${kind}/${id}`, "DELETE");
  };

  // ── Drag reorder: optimistic local update, then persist; revert on failure ──
  const persistReorder = async (url: string, body: unknown) => {
    try {
      const res = await apiFetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(formatApiErrorMessage(data, `Request failed (${res.status})`));
      }
    } catch (e) {
      setError(`Couldn't save the new order: ${formatApiErrorMessage(e, "unknown error")}. Reloaded.`);
      reloadDetail();
    }
  };

  const reorderLevels = (next: Level[]) => {
    if (!detail || selectedCourseId == null) return;
    setDetail({ ...detail, levels: next });
    persistReorder("admin/interactive/levels/reorder", {
      course_id: selectedCourseId,
      ordered_ids: next.map((l) => l.id),
    });
  };

  const reorderModules = (levelId: number | null, next: ModuleNode[]) => {
    if (!detail) return;
    const orderedIds = next.map((m) => m.id);
    const byId = new Map(next.map((m) => [m.id, m]));
    let cursor = 0;
    const modules = detail.modules.map((m) => {
      if ((m.levels?.id ?? null) === levelId) {
        const replacement = byId.get(orderedIds[cursor]);
        cursor += 1;
        return replacement ?? m;
      }
      return m;
    });
    setDetail({ ...detail, modules });
    persistReorder("admin/interactive/modules/reorder", { level_id: levelId, ordered_ids: orderedIds });
  };

  const reorderLessons = (moduleId: number, next: LessonNode[]) => {
    if (!detail) return;
    setDetail({
      ...detail,
      modules: detail.modules.map((m) => (m.id === moduleId ? { ...m, lessons: next } : m)),
    });
    persistReorder("admin/interactive/lessons/reorder", {
      module_id: moduleId,
      ordered_ids: next.map((l) => l.id),
    });
  };

  const modulesForLevel = (levelId: number | null) =>
    (detail?.modules ?? []).filter((m) => (m.levels?.id ?? null) === levelId);

  const toggleOrgSelection = (id: number) => {
    setSelectedOrgIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const newCourseForm = (
    <div className="max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-sm font-semibold text-slate-700">New course</p>
      <div className="mt-3 space-y-3">
        <div>
          <label className="text-xs font-semibold text-slate-600">Course Title</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Course title"
            className={`${fieldClass} mt-1 w-full`}
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-600">Description (Optional)</label>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description"
            className={`${fieldClass} mt-1 w-full`}
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-600">Course Visibility</label>
          <div className="mt-1.5 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setVisibility("public")}
              className={`flex items-center justify-center gap-2 rounded-lg border p-2.5 text-xs font-semibold transition-all ${
                visibility === "public"
                  ? "border-indigo-600 bg-indigo-50/70 text-indigo-700 shadow-sm"
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              <Globe className="h-4 w-4 shrink-0 text-indigo-600" />
              <span>Public (All Orgs)</span>
            </button>
            <button
              type="button"
              onClick={() => setVisibility("restricted")}
              className={`flex items-center justify-center gap-2 rounded-lg border p-2.5 text-xs font-semibold transition-all ${
                visibility === "restricted"
                  ? "border-indigo-600 bg-indigo-50/70 text-indigo-700 shadow-sm"
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              <Lock className="h-4 w-4 shrink-0 text-amber-600" />
              <span>Restricted</span>
            </button>
          </div>
          <p className="mt-1 text-[11px] text-slate-500">
            {visibility === "public"
              ? "Available to all B2B organizations to assign to workspaces, and public learners."
              : "Only visible and assignable to the specific organizations selected below."}
          </p>
        </div>

        {visibility === "restricted" && (
          <div>
            <label className="text-xs font-semibold text-slate-600">
              Select Organizations ({selectedOrgIds.length} selected)
            </label>
            <div className="mt-1.5 max-h-40 overflow-y-auto rounded-lg border border-slate-200 p-2 space-y-1 bg-slate-50/50">
              {orgs.length === 0 ? (
                <p className="text-xs text-slate-400 p-1">No organizations found.</p>
              ) : (
                orgs.map((o) => {
                  const isSelected = selectedOrgIds.includes(o.id);
                  return (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => toggleOrgSelection(o.id)}
                      className={`flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-left text-xs font-medium transition-colors ${
                        isSelected
                          ? "bg-indigo-100 text-indigo-900"
                          : "text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      <span className="truncate">{o.name}</span>
                      {isSelected && <Check className="h-3.5 w-3.5 text-indigo-600 shrink-0" />}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={createCourse}
          disabled={creating}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300 transition-colors shadow-sm"
        >
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Create course
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {error && (
        <p className="rounded-md bg-red-50 px-3 py-2 text-xs font-medium text-red-700">{error}</p>
      )}

      {selectedCourseId == null ? (
        // ── Landing: choose a course (or create one) ──────────────────────────
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {courses.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => selectCourse(c.id)}
                className="group flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-indigo-300 hover:shadow"
              >
                <span className="flex min-w-0 items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                    <GraduationCap className="h-5 w-5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-slate-800">{c.title}</span>
                    <span className="mt-0.5 flex items-center gap-1.5 text-[11px]">
                      {c.isPublic !== false ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                          <Globe className="h-3 w-3" /> Public
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
                          <Lock className="h-3 w-3" /> Restricted
                          {c.orgNames && c.orgNames.length > 0 && ` (${c.orgNames.join(", ")})`}
                        </span>
                      )}
                    </span>
                  </span>
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 group-hover:text-indigo-500" />
              </button>
            ))}
            <button
              type="button"
              onClick={() => setShowDocModal(true)}
              className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-violet-300 bg-violet-50/50 p-4 text-sm font-semibold text-violet-700 transition-all hover:bg-violet-100/70 hover:border-violet-400"
            >
              <Sparkles className="h-4 w-4 text-violet-600" /> AI Generate from Doc
            </button>
            <button
              type="button"
              onClick={() => setShowNewCourse((v) => !v)}
              className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-indigo-300 bg-indigo-50/40 p-4 text-sm font-semibold text-indigo-700 transition-colors hover:bg-indigo-50"
            >
              <Plus className="h-4 w-4" /> New course
            </button>
          </div>
          {showNewCourse && newCourseForm}
          {courses.length === 0 && !showNewCourse && (
            <p className="text-sm text-slate-500">No courses yet — create one to start building.</p>
          )}
        </div>
      ) : loadingDetail && !detail ? (
        <div className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-white p-8 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading course…
        </div>
      ) : !detail ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
          Couldn’t load this course.{" "}
          <button type="button" onClick={() => selectCourse(null)} className="font-semibold text-indigo-600 underline">
            Back to courses
          </button>
        </div>
      ) : (
        // ── Course view: student-style curriculum, drag to reorder ────────────
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => selectCourse(null)}
              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Courses
            </button>
            <h2 className="truncate text-lg font-bold text-slate-900">{detail.title}</h2>
            <Link
              to={`/admin/courses/${detail.id}?view=placement`}
              className="ml-auto shrink-0 rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-800 hover:bg-violet-100"
            >
              Edit placement questions
            </Link>
          </div>

          <SortableList
            items={detail.levels}
            onReorder={reorderLevels}
            className="space-y-4"
            render={(level, handle, li) => (
              <LevelSection
                level={level}
                index={li}
                dragHandle={handle}
                modules={modulesForLevel(level.id)}
                expanded={expanded}
                onToggle={toggleExpand}
                onReorderModules={(next) => reorderModules(level.id, next)}
                onReorderLessons={reorderLessons}
                onOpenLesson={openLesson}
                onAddModule={addModule}
                onAddLesson={addLesson}
                rename={renameItem}
                remove={removeItem}
              />
            )}
          />

          {modulesForLevel(null).length > 0 && (
            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 px-4 py-3">
                <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500">Ungrouped</h3>
              </div>
              <div className="space-y-2 p-3">
                <SortableList
                  items={modulesForLevel(null)}
                  onReorder={(next) => reorderModules(null, next)}
                  className="space-y-2"
                  render={(mod, handle, mi) => (
                    <ModuleAccordion
                      mod={mod}
                      index={mi}
                      dragHandle={handle}
                      open={expanded.has(mod.id)}
                      onToggle={() => toggleExpand(mod.id)}
                      onReorderLessons={reorderLessons}
                      onOpenLesson={openLesson}
                      onAddLesson={addLesson}
                      rename={renameItem}
                      remove={removeItem}
                    />
                  )}
                />
              </div>
            </section>
          )}

          <div className="max-w-sm rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4">
            <AddInline placeholder="New level" onAdd={addLevel} />
          </div>
        </div>
      )}

      <GenerateFromDocModal
        isOpen={showDocModal}
        onClose={() => setShowDocModal(false)}
        onSuccess={async (newId) => {
          await loadCourses();
          selectCourse(newId);
        }}
      />
    </div>
  );
}

function LevelSection({
  level,
  index,
  dragHandle,
  modules,
  expanded,
  onToggle,
  onReorderModules,
  onReorderLessons,
  onOpenLesson,
  onAddModule,
  onAddLesson,
  rename,
  remove,
}: {
  level: Level;
  index: number;
  dragHandle: ReactNode;
  modules: ModuleNode[];
  expanded: Set<number>;
  onToggle: (id: number) => void;
  onReorderModules: (next: ModuleNode[]) => void;
  onReorderLessons: (moduleId: number, next: LessonNode[]) => void;
  onOpenLesson: (id: number) => void;
  onAddModule: (levelId: number, title: string) => Promise<unknown>;
  onAddLesson: (moduleId: number, title: string) => Promise<unknown>;
  rename: (kind: Kind, id: number, field: "name" | "title", current: string) => void;
  remove: (kind: Kind, id: number, label: string) => void;
}) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          {dragHandle}
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-bold text-white">
            {index + 1}
          </span>
          <h3 className="truncate text-sm font-bold uppercase tracking-wide text-slate-700">
            {level.name} <span className="font-medium text-slate-400">· L{level.level_number}</span>
          </h3>
        </div>
        <ItemActions
          onRename={() => rename("levels", level.id, "name", level.name)}
          onDelete={() => remove("levels", level.id, "level")}
        />
      </div>
      <div className="space-y-2 p-3">
        <SortableList
          items={modules}
          onReorder={onReorderModules}
          className="space-y-2"
          render={(mod, handle, mi) => (
            <ModuleAccordion
              mod={mod}
              index={mi}
              dragHandle={handle}
              open={expanded.has(mod.id)}
              onToggle={() => onToggle(mod.id)}
              onReorderLessons={onReorderLessons}
              onOpenLesson={onOpenLesson}
              onAddLesson={onAddLesson}
              rename={rename}
              remove={remove}
            />
          )}
        />
        <AddInline placeholder="New module" onAdd={(t) => onAddModule(level.id, t)} />
      </div>
    </section>
  );
}

function ModuleAccordion({
  mod,
  index,
  dragHandle,
  open,
  onToggle,
  onReorderLessons,
  onOpenLesson,
  onAddLesson,
  rename,
  remove,
}: {
  mod: ModuleNode;
  index: number;
  dragHandle: ReactNode;
  open: boolean;
  onToggle: () => void;
  onReorderLessons: (moduleId: number, next: LessonNode[]) => void;
  onOpenLesson: (id: number) => void;
  onAddLesson: (moduleId: number, title: string) => Promise<unknown>;
  rename: (kind: Kind, id: number, field: "name" | "title", current: string) => void;
  remove: (kind: Kind, id: number, label: string) => void;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/50">
      <div className="flex items-center gap-1.5 px-2 py-2">
        {dragHandle}
        <button type="button" onClick={onToggle} className="flex min-w-0 flex-1 items-center gap-2 text-left">
          <ChevronRight className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-xs font-bold text-indigo-600">
            {index + 1}
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-slate-800">{mod.title}</span>
            <span className="block text-xs text-slate-400">
              {mod.lessons.length} lesson{mod.lessons.length === 1 ? "" : "s"}
            </span>
          </span>
        </button>
        <ItemActions
          onRename={() => rename("modules", mod.id, "title", mod.title)}
          onDelete={() => remove("modules", mod.id, "module")}
        />
      </div>
      {open && (
        <div className="space-y-1 border-t border-slate-100 px-3 py-2 pl-9">
          <SortableList
            items={mod.lessons}
            onReorder={(next) => onReorderLessons(mod.id, next)}
            className="space-y-1"
            render={(lesson, handle) => (
              <LessonRow
                lesson={lesson}
                dragHandle={handle}
                onOpen={() => onOpenLesson(lesson.id)}
                onRename={() => rename("lessons", lesson.id, "title", lesson.title)}
                onDelete={() => remove("lessons", lesson.id, "lesson")}
              />
            )}
          />
          <AddInline placeholder="New lesson" onAdd={(t) => onAddLesson(mod.id, t)} />
        </div>
      )}
    </div>
  );
}

function LessonRow({
  lesson,
  dragHandle,
  onOpen,
  onRename,
  onDelete,
}: {
  lesson: LessonNode;
  dragHandle: ReactNode;
  onOpen: () => void;
  onRename: () => void;
  onDelete: () => void;
}) {
  const hasContent = !!lesson.generated_lessons?.structured_content;
  return (
    <div className="flex items-center gap-1">
      {dragHandle}
      <button
        type="button"
        onClick={onOpen}
        className="flex min-w-0 flex-1 items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm transition-colors hover:border-indigo-300 hover:bg-indigo-50/50"
      >
        <span className="flex min-w-0 items-center gap-2">
          <FileText className="h-3.5 w-3.5 shrink-0 text-slate-400" />
          <span className="truncate text-slate-700">{lesson.title}</span>
          <span
            className={`h-1.5 w-1.5 shrink-0 rounded-full ${hasContent ? "bg-emerald-500" : "bg-slate-300"}`}
            title={hasContent ? "Has content" : "Empty"}
          />
        </span>
        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-400" />
      </button>
      <ItemActions onRename={onRename} onDelete={onDelete} />
    </div>
  );
}

function ItemActions({ onRename, onDelete }: { onRename: () => void; onDelete: () => void }) {
  return (
    <div className="flex shrink-0 items-center gap-0.5">
      <button type="button" onClick={onRename} title="Rename" className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
        <Pencil className="h-3.5 w-3.5" />
      </button>
      <button type="button" onClick={onDelete} title="Delete" className="rounded p-1 text-rose-400 hover:bg-rose-50 hover:text-rose-600">
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function AddInline({
  placeholder,
  onAdd,
}: {
  placeholder: string;
  onAdd: (value: string) => Promise<unknown> | void;
}) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const v = value.trim();
    if (!v) return;
    setBusy(true);
    try {
      await onAdd(v);
      setValue("");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-1.5">
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
        }}
        placeholder={placeholder}
        className={`${fieldClass} min-w-0 flex-1 px-2 py-1 text-xs`}
      />
      <button type="button" onClick={submit} disabled={busy} className={addBtnClass}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}
